type ErrorDetails = {
  type: string;
  message: string;
  trace: string;
};

export class APIError extends Error {
  error: ErrorDetails;
  status: number;
  headers: Headers;

  constructor({
    error,
    status,
    headers,
  }: {
    error: ErrorDetails;
    status: number;
    headers: Headers;
  }) {
    super(error.message);
    this.name = "APIError";
    this.error = error;
    this.status = status;
    this.headers = headers;
  }
}

// This awkward wrapping is to overcome jest's inability to spy on
// window.location.reload().
export const pageReloader = {
  reloadPage: () => {
    window.location.reload();
  },
};

// Likewise, wrap window.location.href assignments so a full-page navigation can
// be spied on in tests (jsdom makes window.location non-configurable and its
// href writes silent no-ops). Route all client-side redirects through this so
// tests can mock it and assert the destination.
export const pageNavigator = {
  navigateTo: (url: string) => {
    window.location.href = url;
  },
};

/**
 * A promise that never settles.
 *
 * Returned to a pending query/mutation once an API error has triggered a
 * full-page navigation (a reload or redirect). Keeping the request pending
 * means the owning component stays on its loading UI rather than briefly
 * rendering an error result before the page changes. A QueryCache `onError`
 * callback can't achieve this: it fires only after observers are notified, so
 * the error has already reached the component by the time it runs. Handling the
 * side effect here, in the request path, runs before the query ever transitions
 * to an error state.
 */
const pendingForever = (): Promise<never> => new Promise<never>(() => {});

/**
 * App-specific error handling, run before the common handling in
 * {@link handleApiError}. Return `true` if the error was handled by initiating a
 * full-page navigation, in which case the originating request is kept pending.
 */
export type ApiErrorHandler = (error: APIError) => boolean;

/**
 * Common API error handling shared by every web app that consumes the query
 * client. Call this from a request wrapper's failure branch instead of throwing
 * directly.
 *
 * - Runs an optional app-specific `handler` first (e.g. Dashboard's 409
 *   organization-switch redirect).
 * - Handles 401 globally by reloading the page so the user re-authenticates.
 * - Re-throws anything else so the query surfaces the error to the component.
 *
 * When a navigation is triggered (by `handler` or the 401 path) this returns a
 * never-settling promise so the request stays pending and no error UI flashes
 * before the page changes.
 */
export async function handleApiError(
  error: APIError,
  handler?: ApiErrorHandler
): Promise<never> {
  if (handler?.(error)) {
    return pendingForever();
  }

  if (error.status === 401) {
    pageReloader.reloadPage();
    return pendingForever();
  }

  throw error;
}
